import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebounceValue';
import { useFetch } from '@/hooks/useFetch';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { GalleryTabsType } from '@/types/common';
import { galleryFileTypes, galleryTabsKeys } from '@/types/common';
import type { GetGalleryResponse } from '@/types/galleryApiTypes';
import {
  FOLDER_ID_QUERY_PARAM,
  GALLERY_CACHE_QUERY_KEY,
  PAGE_QUERY,
  SEARCH_QUERY,
  TAB_QUERY_PARAM,
} from '../../consts/gallery-const';
import type { GetImagesQueryParams } from '../../utils/getImagesQuery';
import { getImagesQuery } from '../../utils/getImagesQuery';

interface UseGetGalleryQueryProps {
  folder_id?: number | null;
  type?: 'image' | 'pdf' | null;
  userId?: number | null;
}

export const useGetGalleryQuery = ({ type, userId }: UseGetGalleryQueryProps = {}) => {
  const [queryFromUrl, updateSearchQueryInUrl] = useSearchQuery();
  const { doFetch } = useFetch<void, GetGalleryResponse>();

  const searchParamFromUrl = queryFromUrl.get(SEARCH_QUERY) || '';
  const tabParamFromUrl = queryFromUrl.get(TAB_QUERY_PARAM) || '';
  const [liveSearchInput, setLiveSearchInput] = useState(searchParamFromUrl);

  useEffect(() => {
    setLiveSearchInput(searchParamFromUrl);
  }, [searchParamFromUrl]);

  useDebouncedValue(liveSearchInput, 500, (debouncedSearchValue) => {
    const currentSearchInUrlWhenDebounceFires = queryFromUrl.get(SEARCH_QUERY) || '';
    if (debouncedSearchValue !== currentSearchInUrlWhenDebounceFires) {
      updateSearchQueryInUrl({
        [SEARCH_QUERY]: debouncedSearchValue || null,
        [PAGE_QUERY]: '1',
      });
    }
  });

  const pageQueryFromUrl = queryFromUrl.get(PAGE_QUERY);
  const folderIdQueryFromUrl = queryFromUrl.get(FOLDER_ID_QUERY_PARAM);
  const currentPage = pageQueryFromUrl ? parseInt(pageQueryFromUrl) : 1;

  const folderIdForApiQuery = folderIdQueryFromUrl ? parseInt(folderIdQueryFromUrl) : null;

  const queryParams: GetImagesQueryParams = {
    folder_id: folderIdForApiQuery,
    user_id: userId ?? undefined,
    search: searchParamFromUrl,
    type,
    page: currentPage,
  };

  const queryKey = [GALLERY_CACHE_QUERY_KEY, queryParams];

  const { data, isLoading, isFetching, error, ...rest } = useQuery<GetGalleryResponse, Error>({
    queryKey,
    queryFn: async () => {
      const url = getImagesQuery(queryParams);
      return doFetch(url);
    },
    placeholderData: keepPreviousData,
    enabled: tabParamFromUrl !== galleryTabsKeys.folders,
    refetchInterval: (query) => {
      if (!query.state.data?.data) return false;

      const hasUnuploadedFiles = query.state.data?.data.some(
        (item) => !item.thumbnail && item.type === galleryFileTypes.IMAGE
      );
      return hasUnuploadedFiles ? 10000 : false;
    },
  });

  const setPageInUrl = (newPage: number) => {
    updateSearchQueryInUrl({ [PAGE_QUERY]: String(newPage) });
  };

  return {
    search: liveSearchInput,
    folderId: folderIdForApiQuery,
    setSearch: setLiveSearchInput,
    page: currentPage,
    setPage: setPageInUrl,
    data,
    isLoading,
    isFetching,
    tabParamFromUrl: tabParamFromUrl as GalleryTabsType,
    error,
    ...rest,
  };
};